import { useEffect } from "react";
import { useAuth, useClerk, UserButton } from "@clerk/react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, CreditCard, Headphones } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { useEntitlement } from "../lib/EntitlementContext";
import { useCoursePurchase } from "../lib/useCoursePurchase";

export default function Dashboard() {
  const { isLoaded, isSignedIn } = useAuth();
  const { openSignIn } = useClerk();
  const navigate = useNavigate();
  const {
    status,
    entitlement,
    hasActiveEntitlement,
    isEntitlementLoading,
  } = useEntitlement();
  const { startPurchase, isPaying, paymentError, paymentMessage } =
    useCoursePurchase();

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      openSignIn();
    }
  }, [isLoaded, isSignedIn, openSignIn]);

  if (!isLoaded || isEntitlementLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-zinc-400">
        Checking account...
      </div>
    );
  }

  if (!isSignedIn || status === "signed_out") {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100">
      <Navbar />
      <main className="mx-auto flex min-h-screen max-w-5xl items-center px-6 py-28">
        <section className="w-full rounded-2xl border border-white/10 bg-white/[0.03] p-6 shadow-2xl shadow-black/30 sm:p-8">
          <div className="flex flex-col gap-5 border-b border-white/10 pb-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059]">
                Account
              </p>
              <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-white">
                Course access
              </h1>
            </div>
            <UserButton afterSignOutUrl="/" />
          </div>

          {hasActiveEntitlement ? (
            <div className="grid gap-8 pt-8 md:grid-cols-[1fr_auto] md:items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1 text-sm text-emerald-200">
                  <CheckCircle2 className="h-4 w-4" />
                  Active subscription
                </div>
                <p className="mt-5 max-w-2xl text-lg leading-8 text-zinc-300">
                  Your purchase is active for{" "}
                  <span className="text-white">
                    {entitlement?.plan || "the masterclass"}
                  </span>
                  . You can enter the studio and continue watching the course.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate("/enter-studio")}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C5A059] px-5 py-3 text-sm font-semibold text-black transition hover:bg-[#d7b76f]"
              >
                <Headphones className="h-4 w-4" />
                Enter Studio
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="grid gap-8 pt-8 md:grid-cols-[1fr_auto] md:items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-[#C5A059]/25 bg-[#C5A059]/10 px-3 py-1 text-sm text-[#E3C985]">
                  <CreditCard className="h-4 w-4" />
                  Purchase required
                </div>
                <p className="mt-5 max-w-2xl text-lg leading-8 text-zinc-300">
                  You are signed in, but this account does not have an active
                  course entitlement yet. Complete purchase to unlock the
                  protected studio lessons.
                </p>
              </div>
              <button
                type="button"
                onClick={startPurchase}
                disabled={isPaying}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C5A059] px-5 py-3 text-sm font-semibold text-black transition hover:bg-[#d7b76f] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <CreditCard className="h-4 w-4" />
                {isPaying ? "Starting checkout..." : "Buy Subscription"}
              </button>
            </div>
          )}

          {(paymentError || paymentMessage) && (
            <div
              role={paymentError ? "alert" : "status"}
              className="mt-6 rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm"
            >
              <p className={paymentError ? "text-red-300" : "text-[#C5A059]"}>
                {paymentError || paymentMessage}
              </p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
